import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import useAnalytics from '../hooks/useAnalytics';
import usePageContent from '../hooks/usePageContent';
import { SEOHead, SCHEMAS } from '../components/seo/SEOHead';
import Header from '../components/landing/Header';
import Footer from '../components/landing/Footer';
import WhatsAppButton from '../components/landing/WhatsAppButton';
import CookieBanner from '../components/landing/CookieBanner';
import { Button } from '../components/ui/button';
import { CinematicHero } from '../components/ui/cinematic-landing-hero';
import {
  ArrowRight, Check, Star, TrendingUp, Clock, FileText, Zap,
  ChevronRight, ChevronLeft, Play, BarChart3, Users, Hammer, Receipt,
  Shield, Award, Bot, CheckCircle2, X, AlertCircle, Lock,
  Globe, Phone, Euro, Search, Plus, Minus, Building2, Wrench,
  ChevronDown, Sparkles
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

// Imágenes Cloudinary optimizadas: w_960 f_webp q_auto (reduce LCP ~70%)
const IMGS = {
  dashboard:    "https://res.cloudinary.com/dnjsjo6yx/image/upload/w_960,f_webp,q_auto/v1770922699/MANDO_Dashboard",
  presupuestos: "https://res.cloudinary.com/dnjsjo6yx/image/upload/w_960,f_webp,q_auto/v1770922699/Captura_de_pantalla_2026-02-12_a_las_19.53.12_e2atlc",
  obras:        "https://res.cloudinary.com/dnjsjo6yx/image/upload/w_960,f_webp,q_auto/v1770922699/Captura_de_pantalla_2026-02-12_a_las_19.53.26_mc06v6",
  rentabilidad: "https://res.cloudinary.com/dnjsjo6yx/image/upload/w_960,f_webp,q_auto/v1770922700/Captura_de_pantalla_2026-02-12_a_las_19.55.39_y9uqff",
  facturas:     "https://res.cloudinary.com/dnjsjo6yx/image/upload/w_960,f_webp,q_auto/v1770922700/Captura_de_pantalla_2026-02-12_a_las_19.54.16_owgvmh",
};

/* ─── HOOKS ─────────────────────────────────────────────────────── */

function useInView(threshold = 0.2) {
  const ref = useRef(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    // Si el elemento ya está visible en el viewport inicial → sin animación de entrada
    const rect = el.getBoundingClientRect();
    if (rect.top < window.innerHeight && rect.bottom > 0) {
      setInView(true);
      return;
    }
    const obs = new IntersectionObserver(
      ([e]) => { if (e.isIntersecting) { setInView(true); obs.disconnect(); } },
      { threshold }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [threshold]);
  return [ref, inView];
}

function useCounter(target, duration = 1600, inView = false) {
  const [count, setCount] = useState(0);
  useEffect(() => {
    if (!inView) return;
    let start = 0;
    const step = Math.ceil(target / (duration / 16));
    const timer = setInterval(() => {
      start += step;
      if (start >= target) { setCount(target); clearInterval(timer); }
      else setCount(start);
    }, 16);
    return () => clearInterval(timer);
  }, [inView, target, duration]);
  return count;
}

function useTypingLoop(messages, speed = 38, pause = 2200) {
  const [msgIdx, setMsgIdx] = useState(0);
  const [displayed, setDisplayed] = useState('');
  const [phase, setPhase] = useState('typing');
  useEffect(() => {
    const full = messages[msgIdx];
    if (phase === 'typing') {
      if (displayed.length < full.length) {
        const t = setTimeout(() => setDisplayed(full.slice(0, displayed.length + 1)), speed);
        return () => clearTimeout(t);
      } else {
        const t = setTimeout(() => setPhase('pausing'), pause);
        return () => clearTimeout(t);
      }
    }
    if (phase === 'pausing') {
      const t = setTimeout(() => { setPhase('erasing'); }, 400);
      return () => clearTimeout(t);
    }
    if (phase === 'erasing') {
      if (displayed.length > 0) {
        const t = setTimeout(() => setDisplayed(prev => prev.slice(0, -1)), 18);
        return () => clearTimeout(t);
      } else {
        setMsgIdx(i => (i + 1) % messages.length);
        setPhase('typing');
      }
    }
  }, [displayed, phase, msgIdx, messages, speed, pause]);
  return { displayed, phase };
}

/* ─── 1. HERO ────────────────────────────────────────────────────── */
const Hero = ({ conversion }) => {
  const navigate = useNavigate();
  const [currentSlide, setCurrentSlide] = useState(0);
  const [hovered, setHovered] = useState(false);
  const slides = [
    { img: IMGS.dashboard,    label: 'Dashboard inteligente',    stat: '100% visibilidad de costes' },
    { img: IMGS.presupuestos, label: 'Presupuestos profesionales', stat: 'PDF en menos de 5 minutos' },
    { img: IMGS.obras,        label: 'Control de obras',           stat: 'Estado en tiempo real' },
    { img: IMGS.rentabilidad, label: 'Rentabilidad real',          stat: 'Margen por obra al instante' },
    { img: IMGS.facturas,     label: 'Facturación automática',     stat: '0€ en facturas olvidadas' },
  ];

  useEffect(() => {
    if (hovered) return;
    const t = setInterval(() => setCurrentSlide(p => (p + 1) % slides.length), 4500);
    return () => clearInterval(t);
  }, [hovered, slides.length]);

  const questions = [
    '¿Cuánto he facturado este mes?',
    '¿Qué obra fue más rentable?',
    '¿Qué facturas están pendientes?',
  ];
  const { displayed } = useTypingLoop(questions, 48, 2000);

  return (
    <section
      id="hero"
      className="relative min-h-screen flex items-center pt-20 overflow-hidden hero-gradient hero-mesh"
      data-testid="hero-section"
    >
      {/* Ambient blobs */}
      <div className="absolute top-1/3 left-1/4 w-[500px] h-[500px] bg-orange-500/5 rounded-full blur-3xl pointer-events-none" />
      <div className="absolute bottom-1/4 right-1/4 w-[400px] h-[400px] bg-[#1E3A5F]/30 rounded-full blur-3xl pointer-events-none" />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-28">
        <div className="grid lg:grid-cols-2 gap-14 items-center">

          {/* LEFT */}
          <div className="space-y-8 animate-fade-left">
            {/* Social proof badge */}
            <div className="inline-flex items-center gap-2.5 bg-white/5 border border-white/10 rounded-full px-5 py-2.5 backdrop-blur-sm">
              <span className="w-2 h-2 bg-emerald-400 rounded-full animate-pulse" />
              <span className="text-slate-300 text-sm font-medium">
                <span className="text-white font-bold">+320 empresas</span>
                {' · '}
                <span className="text-yellow-400">★ 4.9/5</span>
                {' · Sin tarjeta de crédito'}
              </span>
            </div>

            {/* Headline */}
            <div className="space-y-3">
              <h1 className="text-4xl sm:text-5xl lg:text-[3.4rem] font-black text-white leading-[1.08] tracking-tight">
                Deja de perder dinero{' '}
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-orange-400 via-orange-500 to-orange-600 animate-gradient">
                  en obras que creías rentables.
                </span>
              </h1>
              <p className="text-lg sm:text-xl text-slate-400 leading-relaxed max-w-lg">
                MANDO te muestra el margen real de cada obra, en tiempo real.{' '}
                <span className="text-slate-300 font-medium">Sin Excel. Sin adivinar. Sin sorpresas al final.</span>
              </p>
            </div>

            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row gap-4">
              <Button
                size="lg"
                onClick={() => window.open(conversion.app_url || 'https://appmando.leapifyes.com/', '_blank')}
                className="bg-[#FF6B35] hover:bg-[#E85A24] text-white font-bold px-8 py-6 text-lg rounded-[8px] transition-all duration-200 hover:scale-105 hover:shadow-lg hover:shadow-orange-500/30 group"
                data-testid="hero-cta-primary"
              >
                Empieza gratis 15 días
                <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </Button>
              <Button
                size="lg"
                variant="outline"
                onClick={() => window.open(conversion.demo_url || 'https://ricardoserrano-leapifyes.zohobookings.eu/#/242621000000201008', '_blank')}
                className="border-white/20 text-white hover:bg-white/8 hover:border-white/40 px-8 py-6 text-lg rounded-[8px] backdrop-blur-sm"
                data-testid="hero-cta-demo"
              >
                <Play className="mr-2 w-5 h-5 text-orange-400" />
                Ver demo en vivo
              </Button>
            </div>

            {/* Trust micro-copy */}
            <div className="flex flex-wrap gap-x-6 gap-y-2 text-sm text-slate-500">
              {['Sin tarjeta de crédito', 'Sin permanencia', 'Soporte en español', 'Cancela cuando quieras'].map(t => (
                <span key={t} className="flex items-center gap-1.5">
                  <Check className="w-3.5 h-3.5 text-emerald-500" />{t}
                </span>
              ))}
            </div>

            {/* AI teaser mini */}
            <div className="flex items-center gap-3 bg-white/4 border border-white/8 rounded-xl px-4 py-3 max-w-sm backdrop-blur-sm">
              <div className="w-8 h-8 bg-[#2D5FA6] rounded-lg flex items-center justify-center flex-shrink-0">
                <Bot className="w-4 h-4 text-white" />
              </div>
              <div className="min-w-0">
                <span className="text-slate-400 text-xs">MANDO IA pregunta:</span>
                <p className="text-white text-sm font-medium truncate animate-typing-cursor">{displayed}</p>
              </div>
            </div>
          </div>

          {/* RIGHT — Screenshot carousel */}
          <div
            className="relative animate-fade-right delay-200"
            onMouseEnter={() => setHovered(true)}
            onMouseLeave={() => setHovered(false)}
          >
            {/* Glow halo */}
            <div className="absolute -inset-6 bg-gradient-to-r from-orange-500/15 to-[#1E3A5F]/25 rounded-3xl blur-2xl pointer-events-none" />

            <div className="relative rounded-2xl overflow-hidden shadow-2xl shadow-black/60 border border-white/8">
              {/* Browser chrome */}
              <div className="bg-slate-800/90 px-4 py-3 flex items-center gap-3 border-b border-slate-700/60 backdrop-blur-sm">
                <div className="flex gap-1.5">
                  <div className="w-3 h-3 rounded-full bg-red-500/70" />
                  <div className="w-3 h-3 rounded-full bg-yellow-500/70" />
                  <div className="w-3 h-3 rounded-full bg-green-500/70" />
                </div>
                <div className="flex-1 bg-slate-700/60 rounded-md px-3 py-1 text-xs text-slate-400 font-mono">
                  appmando.leapifyes.com
                </div>
                <span className="hidden sm:block text-xs text-slate-500">{slides[currentSlide].label}</span>
              </div>

              <div className="relative aspect-video bg-slate-900">
                {slides.map((s, i) => (
                  <img
                    key={i}
                    src={s.img}
                    alt={`Vista real MANDO — ${s.label}`}
                    className={`absolute inset-0 w-full h-full object-cover transition-all duration-700 ${
                      i === currentSlide ? 'opacity-100 scale-100' : 'opacity-0 scale-[1.02]'
                    }`}
                    loading={i === 0 ? 'eager' : 'lazy'}
                  />
                ))}

                {/* Slide dots */}
                <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2 z-10">
                  {slides.map((_, i) => (
                    <button
                      key={i}
                      onClick={() => setCurrentSlide(i)}
                      aria-label={`Slide ${i + 1}`}
                      className={`rounded-full transition-all duration-300 ${i === currentSlide ? 'w-6 h-2 bg-orange-500' : 'w-2 h-2 bg-white/30 hover:bg-white/50'}`}
                    />
                  ))}
                </div>

                {/* Nav arrows (show on hover) */}
                <button
                  onClick={() => setCurrentSlide(p => (p - 1 + slides.length) % slides.length)}
                  className={`absolute left-3 top-1/2 -translate-y-1/2 z-10 w-9 h-9 bg-black/40 backdrop-blur-sm rounded-full flex items-center justify-center text-white/70 hover:text-white hover:bg-black/60 transition-all duration-200 ${hovered ? 'opacity-100' : 'opacity-0'}`}
                >
                  <ChevronLeft className="w-4 h-4" />
                </button>
                <button
                  onClick={() => setCurrentSlide(p => (p + 1) % slides.length)}
                  className={`absolute right-3 top-1/2 -translate-y-1/2 z-10 w-9 h-9 bg-black/40 backdrop-blur-sm rounded-full flex items-center justify-center text-white/70 hover:text-white hover:bg-black/60 transition-all duration-200 ${hovered ? 'opacity-100' : 'opacity-0'}`}
                >
                  <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            </div>

            {/* Floating stat badge */}
            <div className="absolute -bottom-4 -right-3 sm:-right-6 bg-slate-900/95 border border-slate-700 rounded-xl px-4 py-3 shadow-xl backdrop-blur-xl z-20 animate-float">
              <div className="text-emerald-400 text-sm font-bold">{slides[currentSlide].stat}</div>
              <div className="text-slate-500 text-xs">Vista real del sistema</div>
            </div>

            {/* Companies badge */}
            <div className="absolute -top-4 -left-3 sm:-left-6 bg-slate-900/95 border border-slate-700 rounded-xl px-4 py-3 shadow-xl backdrop-blur-xl z-20">
              <div className="flex items-center gap-2">
                <div className="flex -space-x-1.5">
                  {['CG','ML','JP','AR'].map((ini, i) => (
                    <div key={i} className="w-7 h-7 rounded-full bg-gradient-to-br from-[#1E3A5F] to-[#2D5FA6] border-2 border-slate-900 flex items-center justify-center text-white text-[9px] font-bold">{ini}</div>
                  ))}
                </div>
                <div>
                  <div className="text-white text-xs font-semibold">+320 empresas</div>
                  <div className="flex gap-0.5">{[1,2,3,4,5].map(s => <Star key={s} className="w-2.5 h-2.5 text-yellow-400 fill-yellow-400" />)}</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

/* ─── 2. MÉTRICAS ANIMADAS ───────────────────────────────────────── */
const MetricsBand = () => {
  const [ref, inView] = useInView(0.3);

  const c1 = useCounter(40,  1400, inView);
  const c2 = useCounter(5,   800,  inView);
  const c3 = useCounter(320, 1600, inView);

  const metrics = [
    { icon: Clock,      color: 'text-orange-400', value: `${c1}%`,    label: 'menos tiempo administrativo',    desc: 'Tareas que antes hacías a mano, automatizadas' },
    { icon: FileText,   color: 'text-blue-400',   value: `${c2} min`, label: 'para un presupuesto profesional', desc: 'PDF con tu marca, partidas desglosadas y precio' },
    { icon: Euro,       color: 'text-emerald-400',value: '0€',         label: 'en facturas olvidadas',           desc: 'Sistema de alertas automáticas de cobro pendiente' },
    { icon: TrendingUp, color: 'text-purple-400', value: '100%',       label: 'visibilidad de costes',           desc: 'El margen real de cada obra, en tiempo real' },
    { icon: Users,      color: 'text-yellow-400', value: `+${c3}`,    label: 'empresas confían en MANDO',        desc: 'Reformas, fontanería, electricidad, construcción' },
    { icon: Star,       color: 'text-pink-400',   value: '4.9/5',      label: '· 120+ reseñas verificadas',      desc: 'Valoración media en Google y App Stores' },
  ];

  return (
    <section ref={ref} className="py-16 bg-[#080d1a] border-y border-white/5">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
          {metrics.map((m, i) => {
            const Icon = m.icon;
            return (
              <div
                key={i}
                className={`metric-card rounded-[12px] p-5 text-center reveal ${inView ? 'visible' : ''}`}
                style={{ transitionDelay: `${i * 80}ms` }}
              >
                <Icon className={`w-6 h-6 ${m.color} mx-auto mb-3`} />
                <div className="text-2xl font-black text-white mb-1">{m.value}</div>
                <div className="text-slate-400 text-xs leading-tight">{m.label}</div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

/* ─── 3. PROBLEMS ────────────────────────────────────────────────── */
const Problems = () => {
  const [ref, inView] = useInView(0.15);
  const problems = [
    { emoji: '📋', title: 'Presupuestos en Excel', desc: 'Horas editando hojas de cálculo sin logo, sin PDF, sin control de versiones.' },
    { emoji: '📱', title: 'Obras por WhatsApp',    desc: 'Sin trazabilidad, sin histórico, sin saber quién hace qué.' },
    { emoji: '💸', title: 'Márgenes invisibles',   desc: 'No sabes si una obra fue rentable hasta que ya es tarde para reaccionar.' },
    { emoji: '🗂️', title: 'Facturas perdidas',     desc: 'Cobros que se olvidan semanas. Dinero tuyo que nunca llega.' },
    { emoji: '📉', title: 'Gastos sin control',     desc: 'Los márgenes se comen solos porque nadie registra los gastos de obra.' },
    { emoji: '🕐', title: 'Horas en papeleo',       desc: 'Tiempo en tareas administrativas que podrías invertir en más obras.' },
  ];

  return (
    <section id="problemas" className="py-24 bg-white" ref={ref}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className={`text-center mb-14 reveal ${inView ? 'visible' : ''}`}>
          <span className="text-orange-500 font-semibold text-sm uppercase tracking-wider">El problema real</span>
          <h2 className="text-3xl sm:text-4xl font-black text-slate-900 mt-3 mb-4">
            ¿Te suena alguno de estos problemas?
          </h2>
          <p className="text-xl text-slate-500 max-w-xl mx-auto">
            Si gestionas obras y reformas, es probable que estés pagando el precio de no tener el sistema adecuado.
          </p>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {problems.map((p, i) => (
            <div
              key={i}
              className={`problem-card group relative p-6 bg-slate-50 border-2 border-slate-200 rounded-[12px] cursor-default card-hover reveal ${inView ? 'visible' : ''}`}
              style={{ transitionDelay: `${i * 80}ms` }}
            >
              {/* Hover overlay */}
              <div className="check-overlay rounded-[10px] border-2">
                <div className="flex flex-col items-center gap-2 z-10 relative">
                  <CheckCircle2 className="w-12 h-12 text-emerald-500" strokeWidth={1.5} />
                  <span className="text-emerald-700 font-bold text-sm">MANDO lo resuelve</span>
                </div>
              </div>
              <span className="text-3xl mb-4 block">{p.emoji}</span>
              <h3 className="text-slate-900 font-bold text-base mb-2">{p.title}</h3>
              <p className="text-slate-500 text-sm leading-relaxed">{p.desc}</p>
            </div>
          ))}
        </div>

        <div className={`text-center mt-12 reveal ${inView ? 'visible' : ''}`} style={{ transitionDelay: '500ms' }}>
          <p className="text-slate-600 text-lg">
            <strong className="text-slate-900">MANDO resuelve los 6.</strong>{' '}
            Esta semana.{' '}
            <span className="text-orange-500 font-semibold">Sin formación técnica.</span>
          </p>
        </div>
      </div>
    </section>
  );
};

/* ─── 4. IA SECTION ──────────────────────────────────────────────── */
const AISection = ({ navigate }) => {
  const [ref, inView] = useInView(0.2);
  const [activeMsg, setActiveMsg] = useState(0);

  const conversation = [
    { from: 'user', msg: '¿Cuánto tengo pendiente de cobrar?' },
    { from: 'ai',   msg: 'Tienes 8.200€ en 3 facturas pendientes. La más urgente es Ref. 2024-032 por 3.500€ que vence esta semana. ¿Preparo un recordatorio para el cliente?' },
    { from: 'user', msg: '¿Cuál es mi obra más rentable este mes?' },
    { from: 'ai',   msg: 'La obra "Reforma Travessera 45" con un margen del 34%, muy por encima de tu media del 28%. El control de gastos allí fue excelente.' },
  ];

  useEffect(() => {
    if (!inView) return;
    const t = setInterval(() => setActiveMsg(p => (p < conversation.length - 1 ? p + 1 : 0)), 2200);
    return () => clearInterval(t);
  }, [inView, conversation.length]);

  const aiFeatures = [
    { icon: Bot,       title: 'Chat con tus datos',        desc: 'Pregunta en lenguaje natural sobre obras, facturas y márgenes.' },
    { icon: FileText,  title: 'OCR automático',             desc: 'Sube facturas de proveedor y la IA extrae los datos al instante.' },
    { icon: TrendingUp,title: 'Baremos de mercado',         desc: 'Precios de referencia del sector para presupuestar con datos reales.' },
    { icon: Zap,       title: 'Asesor financiero IA',       desc: 'Análisis de rentabilidad y recomendaciones para mejorar tus márgenes.' },
  ];

  return (
    <section id="ia" className="py-24 relative overflow-hidden" ref={ref} style={{ background: 'linear-gradient(160deg, #080d1a 0%, #0d1424 50%, #0a0e1c 100%)' }}>
      {/* Background accent */}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[400px] bg-[#2D5FA6]/8 rounded-full blur-3xl pointer-events-none" />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className={`text-center mb-16 reveal ${inView ? 'visible' : ''}`}>
          <div className="inline-flex items-center gap-2 bg-[#2D5FA6]/20 border border-[#2D5FA6]/30 rounded-full px-4 py-2 mb-6">
            <Sparkles className="w-4 h-4 text-blue-400" />
            <span className="text-blue-300 text-sm font-medium">IA incluida en planes PRO, BUSINESS y ELITE</span>
          </div>
          <h2 className="text-3xl sm:text-5xl font-black text-white mb-5 leading-tight">
            La primera IA que conoce
            <span className="block text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-cyan-400">
              tu negocio de reformas.
            </span>
          </h2>
          <p className="text-xl text-slate-400 max-w-2xl mx-auto">
            No es ChatGPT genérico. MANDO IA tiene acceso a tus obras, facturas, márgenes y gastos reales.
            Pregúntale lo que necesites — responde en segundos.
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-12 items-start">
          {/* Chat demo */}
          <div className={`reveal ${inView ? 'visible' : ''} delay-200`}>
            <div className="bg-slate-800/60 backdrop-blur-xl rounded-2xl border border-slate-700/50 overflow-hidden shadow-2xl">
              {/* Chat header */}
              <div className="bg-slate-900/80 px-5 py-4 flex items-center gap-3 border-b border-slate-700/50">
                <div className="relative">
                  <div className="w-10 h-10 bg-gradient-to-br from-[#2D5FA6] to-blue-500 rounded-xl flex items-center justify-center">
                    <Bot className="w-5 h-5 text-white" />
                  </div>
                  <div className="absolute -bottom-0.5 -right-0.5 w-3 h-3 bg-emerald-400 rounded-full border-2 border-slate-900" />
                </div>
                <div>
                  <div className="text-white font-bold text-sm">MANDO IA</div>
                  <div className="text-emerald-400 text-xs">En línea · Conectado a tus datos</div>
                </div>
                <div className="ml-auto text-xs text-slate-500 bg-slate-800 px-3 py-1 rounded-full border border-slate-700">
                  Demo: Reformas García
                </div>
              </div>

              {/* Messages */}
              <div className="p-5 space-y-4 min-h-[280px]">
                {conversation.slice(0, activeMsg + 1).map((m, i) => (
                  <div
                    key={i}
                    className={`flex ${m.from === 'user' ? 'justify-end' : 'justify-start'} animate-fade-up`}
                  >
                    {m.from === 'ai' && (
                      <div className="w-7 h-7 bg-[#2D5FA6] rounded-lg flex items-center justify-center mr-2 flex-shrink-0 mt-0.5">
                        <Bot className="w-3.5 h-3.5 text-white" />
                      </div>
                    )}
                    <div className={`max-w-[82%] px-4 py-3 rounded-2xl text-sm leading-relaxed ${
                      m.from === 'user'
                        ? 'bg-[#2D5FA6] text-white rounded-br-sm'
                        : 'bg-slate-700/80 text-slate-200 rounded-bl-sm border border-slate-600/40'
                    }`}>
                      {m.msg}
                    </div>
                  </div>
                ))}
                {activeMsg < conversation.length - 1 && (
                  <div className="flex justify-start">
                    <div className="w-7 h-7 bg-[#2D5FA6] rounded-lg flex items-center justify-center mr-2 flex-shrink-0">
                      <Bot className="w-3.5 h-3.5 text-white" />
                    </div>
                    <div className="bg-slate-700/80 border border-slate-600/40 px-4 py-3 rounded-2xl rounded-bl-sm flex gap-1.5 items-center">
                      {[0,1,2].map(i => (
                        <div key={i} className="w-1.5 h-1.5 bg-slate-400 rounded-full animate-bounce" style={{ animationDelay: `${i * 150}ms` }} />
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* Input mock */}
              <div className="px-5 pb-5">
                <div className="bg-slate-700/40 border border-slate-600/40 rounded-xl px-4 py-3 flex items-center justify-between">
                  <span className="text-slate-500 text-sm">Pregúntame sobre tu negocio...</span>
                  <ArrowRight className="w-4 h-4 text-slate-600" />
                </div>
              </div>
            </div>

            <Button
              onClick={() => navigate('/inteligencia-artificial')}
              className="mt-6 w-full bg-gradient-to-r from-[#2D5FA6] to-blue-500 hover:from-[#1E3A5F] hover:to-[#2D5FA6] text-white font-bold py-4 rounded-[8px] transition-all duration-200 hover:scale-[1.02]"
            >
              <Sparkles className="mr-2 w-4 h-4" />
              Prueba la IA gratis →
            </Button>
          </div>

          {/* AI features */}
          <div className="space-y-5">
            {aiFeatures.map((f, i) => {
              const Icon = f.icon;
              return (
                <div
                  key={i}
                  className={`flex gap-4 p-5 bg-white/3 border border-white/7 rounded-[12px] card-hover reveal ${inView ? 'visible' : ''}`}
                  style={{ transitionDelay: `${i * 100 + 300}ms` }}
                >
                  <div className="w-12 h-12 bg-[#2D5FA6]/20 border border-[#2D5FA6]/30 rounded-xl flex items-center justify-center flex-shrink-0">
                    <Icon className="w-6 h-6 text-blue-400" />
                  </div>
                  <div>
                    <h3 className="text-white font-bold mb-1">{f.title}</h3>
                    <p className="text-slate-400 text-sm leading-relaxed">{f.desc}</p>
                  </div>
                </div>
              );
            })}

            <div className="mt-6 p-5 bg-gradient-to-r from-orange-500/10 to-orange-400/5 border border-orange-500/20 rounded-[12px]">
              <div className="flex items-start gap-3">
                <Award className="w-5 h-5 text-orange-400 flex-shrink-0 mt-0.5" />
                <p className="text-slate-300 text-sm leading-relaxed">
                  <strong className="text-white">No es IA genérica.</strong> MANDO IA tiene acceso directo
                  a tus obras activas, facturas, márgenes y gastos reales.
                  Como tener un gestor disponible 24/7 que conoce tu empresa.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

/* ─── 5. MÓDULOS ─────────────────────────────────────────────────── */
const Modules = ({ navigate }) => {
  const [ref, inView] = useInView(0.1);
  const [filter, setFilter] = useState('Todos');
  const [active, setActive] = useState(0);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerModule, setDrawerModule] = useState(null);

  const allModules = [
    { name: 'Dashboard',      icon: BarChart3,  img: IMGS.dashboard,    cat: 'Gestión',  desc: 'KPIs en tiempo real: ingresos, gastos, márgenes y obras activas. Todo en una sola pantalla, siempre actualizado.' },
    { name: 'Presupuestos',   icon: FileText,   img: IMGS.presupuestos, cat: 'Gestión',  desc: 'Crea presupuestos profesionales con partidas desglosadas y genera PDF con tu logo en menos de 5 minutos.' },
    { name: 'Obras',          icon: Hammer,     img: IMGS.obras,        cat: 'Obras',    desc: 'Seguimiento completo de cada proyecto: fases, tareas, responsables y estado en tiempo real.' },
    { name: 'Facturas',       icon: Receipt,    img: IMGS.facturas,     cat: 'Finanzas', desc: 'Facturación profesional conectada con tus presupuestos y obras. Alertas de cobros pendientes automáticas.' },
    { name: 'Rentabilidad',   icon: TrendingUp, img: IMGS.rentabilidad, cat: 'Finanzas', desc: 'Margen real por obra calculado automáticamente. Compara estimado vs. real y detecta dónde pierdes dinero.' },
    { name: 'Clientes',       icon: Users,      img: IMGS.dashboard,    cat: 'Gestión',  desc: 'CRM integrado con historial completo: presupuestos, obras y facturas de cada cliente en un solo lugar.' },
    { name: 'Gastos',         icon: Euro,       img: IMGS.rentabilidad, cat: 'Finanzas', desc: 'Registro de gastos por obra y categoría. Controla lo que gastas antes de que afecte al margen.' },
    { name: 'Stock',          icon: Building2,  img: IMGS.obras,        cat: 'Obras',    desc: 'Control de materiales y almacén. Asigna materiales a obras y evita compras duplicadas.' },
    { name: 'OCR IA',         icon: Bot,        img: IMGS.dashboard,    cat: 'IA',       desc: 'Sube una factura de proveedor y la IA extrae todos los datos automáticamente en segundos.' },
    { name: 'Baremos',        icon: Award,      img: IMGS.presupuestos, cat: 'IA',       desc: 'Precios de referencia del sector actualizado. Presupuesta con datos reales, no a ojo.' },
    { name: 'MANDO IA Chat',  icon: Sparkles,   img: IMGS.facturas,     cat: 'IA',       desc: 'Pregunta en lenguaje natural sobre tu negocio. Responde con tus datos reales en segundos.' },
    { name: 'Incidencias',    icon: AlertCircle,img: IMGS.obras,        cat: 'Obras',    desc: 'Gestión de incidencias y garantías post-obra. Registra, asigna y cierra con total trazabilidad.' },
    { name: 'Proveedores',    icon: Wrench,     img: IMGS.dashboard,    cat: 'Finanzas', desc: 'Control de pagos a proveedores vinculados a obras. Evita descuadres entre lo que entra y lo que sale.' },
  ];

  const tabs = ['Todos', 'Gestión', 'Finanzas', 'Obras', 'IA'];
  const filtered = filter === 'Todos' ? allModules : allModules.filter(m => m.cat === filter);
  const display = filtered[active] || filtered[0];

  const openDrawer = (mod) => { setDrawerModule(mod); setDrawerOpen(true); };

  return (
    <section id="modulos" className="py-24 bg-slate-50" ref={ref}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className={`text-center mb-12 reveal ${inView ? 'visible' : ''}`}>
          <span className="text-orange-500 font-semibold text-sm uppercase tracking-wider">Todo integrado</span>
          <h2 className="text-3xl sm:text-4xl font-black text-slate-900 mt-3 mb-4">
            13 módulos. Un solo sistema.
          </h2>
          <p className="text-xl text-slate-500 max-w-xl mx-auto">
            Cada módulo resuelve un problema real. Todos conectados entre sí. Sin duplicar datos.
          </p>
        </div>

        {/* Filter tabs */}
        <div className={`flex flex-wrap justify-center gap-2 mb-10 reveal ${inView ? 'visible' : ''} delay-100`}>
          {tabs.map(tab => (
            <button
              key={tab}
              onClick={() => { setFilter(tab); setActive(0); }}
              className={`px-5 py-2 rounded-full text-sm font-semibold transition-all duration-200 ${
                filter === tab
                  ? 'bg-orange-500 text-white shadow-md shadow-orange-500/25'
                  : 'bg-white text-slate-600 border border-slate-200 hover:border-orange-300 hover:text-orange-600'
              }`}
            >
              {tab === 'IA' && '🤖 '}{tab}
            </button>
          ))}
        </div>

        {/* Grid */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3 mb-10">
          {filtered.map((m, i) => {
            const Icon = m.icon;
            const isActive = active === i;
            return (
              <button
                key={`${filter}-${i}`}
                onClick={() => { setActive(i); openDrawer(m); }}
                className={`group flex flex-col items-center gap-2.5 p-4 rounded-[12px] border-2 transition-all duration-200 text-center reveal ${inView ? 'visible' : ''} ${
                  isActive
                    ? 'border-orange-500 bg-orange-50 shadow-md shadow-orange-500/10'
                    : 'border-slate-200 bg-white hover:border-orange-300 hover:shadow-sm'
                }`}
                style={{ transitionDelay: `${i * 40}ms` }}
              >
                <div className={`w-10 h-10 rounded-xl flex items-center justify-center transition-colors ${isActive ? 'bg-orange-100' : 'bg-slate-100 group-hover:bg-orange-50'}`}>
                  <Icon className={`w-5 h-5 ${isActive ? 'text-orange-500' : 'text-slate-500 group-hover:text-orange-500'}`} />
                </div>
                <span className={`font-semibold text-xs leading-tight ${isActive ? 'text-orange-600' : 'text-slate-700'}`}>{m.name}</span>
                <span className={`text-[10px] px-2 py-0.5 rounded-full ${
                  m.cat === 'IA' ? 'bg-blue-100 text-blue-600' :
                  m.cat === 'Finanzas' ? 'bg-green-100 text-green-600' :
                  m.cat === 'Obras' ? 'bg-purple-100 text-purple-600' :
                  'bg-slate-100 text-slate-500'
                }`}>{m.cat}</span>
              </button>
            );
          })}
        </div>

        {/* Preview */}
        <div className={`grid lg:grid-cols-2 gap-10 items-center mt-4 reveal ${inView ? 'visible' : ''} delay-300`}>
          <div className="relative rounded-2xl overflow-hidden shadow-xl border border-slate-200">
            <div className="bg-slate-100 px-4 py-2.5 flex items-center gap-2 border-b border-slate-200">
              <div className="flex gap-1.5">
                {['bg-red-400','bg-yellow-400','bg-green-400'].map(c => <div key={c} className={`w-3 h-3 rounded-full ${c}/70`} />)}
              </div>
              <div className="flex-1 bg-white rounded px-3 py-0.5 text-xs text-slate-400 font-mono">appmando.leapifyes.com</div>
            </div>
            <img src={display.img} alt={`MANDO — ${display.name}`} className="w-full h-auto" loading="lazy" />
          </div>
          <div className="space-y-5">
            <div className="flex items-center gap-3">
              {React.createElement(display.icon, { className: 'w-9 h-9 text-orange-500' })}
              <h3 className="text-2xl font-black text-slate-900">{display.name}</h3>
            </div>
            <p className="text-lg text-slate-600 leading-relaxed">{display.desc}</p>
            <Button onClick={() => navigate('/funcionalidades')} variant="outline" className="border-slate-300 text-slate-700 hover:border-orange-500 hover:text-orange-600 rounded-[8px]">
              Ver todos los módulos <ChevronRight className="ml-1 w-4 h-4" />
            </Button>
          </div>
        </div>
      </div>

      {/* Drawer */}
      {drawerOpen && drawerModule && (
        <div className="fixed inset-0 z-50 flex justify-end" onClick={() => setDrawerOpen(false)}>
          <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" />
          <div className="relative bg-white w-full max-w-md h-full shadow-2xl flex flex-col animate-fade-right" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between p-6 border-b border-slate-100">
              <div className="flex items-center gap-3">
                {React.createElement(drawerModule.icon, { className: 'w-6 h-6 text-orange-500' })}
                <h3 className="text-slate-900 font-black text-lg">{drawerModule.name}</h3>
              </div>
              <button onClick={() => setDrawerOpen(false)} className="p-2 text-slate-400 hover:text-slate-700 hover:bg-slate-100 rounded-lg transition-colors">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto p-6">
              <img src={drawerModule.img} alt={drawerModule.name} className="w-full rounded-xl border border-slate-200 shadow-sm mb-6" loading="lazy" />
              <span className={`text-xs px-3 py-1 rounded-full font-semibold mb-4 inline-block ${
                drawerModule.cat === 'IA' ? 'bg-blue-100 text-blue-600' :
                drawerModule.cat === 'Finanzas' ? 'bg-green-100 text-green-600' :
                drawerModule.cat === 'Obras' ? 'bg-purple-100 text-purple-600' :
                'bg-slate-100 text-slate-600'
              }`}>{drawerModule.cat}</span>
              <p className="text-slate-600 leading-relaxed text-base mt-3">{drawerModule.desc}</p>
            </div>
            <div className="p-6 border-t border-slate-100">
              <Button onClick={() => navigate('/funcionalidades')} className="w-full bg-orange-500 hover:bg-orange-600 text-white rounded-[8px]">
                Ver módulo completo <ArrowRight className="ml-2 w-4 h-4" />
              </Button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
};

/* ─── 6. COMPARATIVA ─────────────────────────────────────────────── */
const Comparison = () => {
  const [ref, inView] = useInView(0.2);
  const rows = [
    { feature: 'IA integrada con tus datos',    mando: true,  excel: false, erp: false },
    { feature: 'Especializado en reformas',      mando: true,  excel: false, erp: false },
    { feature: 'PDF de presupuesto en 5 min',    mando: true,  excel: false, erp: 'partial' },
    { feature: 'Control de margen por obra',     mando: true,  excel: false, erp: 'partial' },
    { feature: 'OCR de facturas automático',     mando: true,  excel: false, erp: false },
    { feature: 'Precio accesible (desde 59€)',   mando: true,  excel: true,  erp: false },
    { feature: 'Soporte en español incluido',    mando: true,  excel: false, erp: 'partial' },
    { feature: 'Prueba gratuita sin tarjeta',    mando: true,  excel: true,  erp: false },
    { feature: 'Sin permanencia',                mando: true,  excel: true,  erp: false },
    { feature: 'App móvil nativa',               mando: true,  excel: false, erp: 'partial' },
  ];

  const Icon = ({ v }) => {
    if (v === true)      return <CheckCircle2 className="w-5 h-5 text-emerald-500 mx-auto" />;
    if (v === false)     return <X className="w-5 h-5 text-red-400 mx-auto" />;
    if (v === 'partial') return <AlertCircle className="w-5 h-5 text-yellow-400 mx-auto" />;
    return null;
  };

  return (
    <section id="comparativa" className="py-24 bg-white" ref={ref}>
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className={`text-center mb-14 reveal ${inView ? 'visible' : ''}`}>
          <span className="text-orange-500 font-semibold text-sm uppercase tracking-wider">Comparativa honesta</span>
          <h2 className="text-3xl sm:text-4xl font-black text-slate-900 mt-3 mb-4">
            ¿Por qué MANDO y no otra opción?
          </h2>
          <p className="text-xl text-slate-500 max-w-xl mx-auto">
            Excel no es un ERP. Los ERP genéricos no entienden reformas. MANDO sí.
          </p>
        </div>

        <div className={`rounded-2xl border border-slate-200 overflow-hidden shadow-lg reveal ${inView ? 'visible' : ''} delay-200`}>
          {/* Header */}
          <div className="grid grid-cols-4 bg-slate-50 border-b border-slate-200">
            <div className="p-5 text-sm font-semibold text-slate-500">Característica</div>
            <div className="p-5 text-center comparison-mando border-l border-slate-200">
              <div className="font-black text-orange-500 text-base">MANDO</div>
              <div className="text-xs text-slate-500">ERP para reformas</div>
            </div>
            <div className="p-5 text-center border-l border-slate-200">
              <div className="font-bold text-slate-700">Excel</div>
              <div className="text-xs text-slate-400">Hoja de cálculo</div>
            </div>
            <div className="p-5 text-center border-l border-slate-200">
              <div className="font-bold text-slate-700">ERP genérico</div>
              <div className="text-xs text-slate-400">SAP / Holded / etc.</div>
            </div>
          </div>

          {rows.map((row, i) => (
            <div
              key={i}
              className={`grid grid-cols-4 border-b border-slate-100 last:border-0 ${i % 2 === 0 ? 'bg-white' : 'bg-slate-50/50'} reveal ${inView ? 'visible' : ''}`}
              style={{ transitionDelay: `${i * 50 + 250}ms` }}
            >
              <div className="p-4 text-sm text-slate-700 font-medium flex items-center gap-2">
                {row.feature}
              </div>
              <div className="p-4 comparison-mando border-l border-slate-100 flex items-center justify-center">
                <Icon v={row.mando} />
              </div>
              <div className="p-4 border-l border-slate-100 flex items-center justify-center">
                <Icon v={row.excel} />
              </div>
              <div className="p-4 border-l border-slate-100 flex items-center justify-center">
                <Icon v={row.erp} />
              </div>
            </div>
          ))}
        </div>

        <div className={`flex items-center gap-6 justify-center mt-6 text-xs text-slate-400 reveal ${inView ? 'visible' : ''} delay-500`}>
          <span className="flex items-center gap-1.5"><CheckCircle2 className="w-3.5 h-3.5 text-emerald-500" />Incluido</span>
          <span className="flex items-center gap-1.5"><AlertCircle className="w-3.5 h-3.5 text-yellow-400" />Parcial o de pago</span>
          <span className="flex items-center gap-1.5"><X className="w-3.5 h-3.5 text-red-400" />No disponible</span>
        </div>
      </div>
    </section>
  );
};

/* ─── 7. TESTIMONIOS CARRUSEL ────────────────────────────────────── */
const Testimonials = () => {
  const [ref, inView] = useInView(0.2);
  const [idx, setIdx] = useState(0);
  const [paused, setPaused] = useState(false);
  const [testimonials, setTestimonials] = useState([
    { name: 'Carlos García',  role: 'Propietario',    company: 'Reformas García Barcelona',  content: 'Antes tardaba 2 horas en hacer un presupuesto. Con MANDO lo tengo en 10 minutos con PDF incluido. Ha cambiado completamente la forma en que gestiono mi empresa.', rating: 5, avatar_initials: 'CG' },
    { name: 'María López',    role: 'Gerente',         company: 'Instalaciones López e Hijos', content: 'El control de obras y el seguimiento de facturas pendientes nos ha evitado perder más de 8.000€ en cobros olvidados el primer año. Es indispensable.', rating: 5, avatar_initials: 'ML' },
    { name: 'Jordi Puig',     role: 'Director',        company: 'Construcciones Puig SL',     content: 'La calculadora de rentabilidad y los baremos de mercado son oro puro. Por fin presupuesto con datos reales, no a ojo. El margen ha subido un 12%.', rating: 5, avatar_initials: 'JP' },
    { name: 'Ana Rodríguez',  role: 'Administradora',  company: 'Reformas Rodo Madrid',       content: 'Llevamos 6 meses y el ahorro en tiempo administrativo es brutal. La IA que escanea facturas de proveedores nos ahorra 3 horas semanales como mínimo.', rating: 5, avatar_initials: 'AR' },
    { name: 'Pep Martínez',   role: 'CEO',             company: 'Servicios Técnicos Martínez', content: 'Intenté otros sistemas antes. Ninguno entendía el sector de reformas. MANDO habla nuestro idioma: presupuestos, obras, certificaciones, márgenes.', rating: 5, avatar_initials: 'PM' },
  ]);

  useEffect(() => {
    fetch(`${API_URL}/api/public/testimonials`).then(r => r.ok ? r.json() : null).then(d => { if (d && d.length > 0) setTestimonials(d); }).catch(() => {});
  }, []);

  useEffect(() => {
    if (paused || !inView) return;
    const t = setInterval(() => setIdx(p => (p + 1) % testimonials.length), 5000);
    return () => clearInterval(t);
  }, [paused, inView, testimonials.length]);

  const visible = testimonials.filter(t => t.enabled !== false);

  return (
    <section id="testimonios" className="py-24 bg-[#0a0f1e]" ref={ref}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className={`text-center mb-14 reveal ${inView ? 'visible' : ''}`}>
          <span className="text-orange-500 font-semibold text-sm uppercase tracking-wider">Testimonios verificados</span>
          <h2 className="text-3xl sm:text-4xl font-black text-white mt-3 mb-4">
            Lo que dicen las empresas que usan MANDO
          </h2>
          <div className="flex items-center justify-center gap-2 mt-2">
            {[1,2,3,4,5].map(s => <Star key={s} className="w-5 h-5 text-yellow-400 fill-yellow-400" />)}
            <span className="text-slate-400 text-sm ml-2">4.9/5 · 120+ reseñas verificadas</span>
          </div>
        </div>

        {/* Main testimonial spotlight */}
        <div
          className={`max-w-3xl mx-auto mb-10 reveal ${inView ? 'visible' : ''} delay-200`}
          onMouseEnter={() => setPaused(true)}
          onMouseLeave={() => setPaused(false)}
        >
          {visible.length > 0 && (
            <div key={idx} className="bg-white/5 border border-white/10 rounded-2xl p-8 sm:p-10 text-center testimonial-slide">
              <div className="flex items-center justify-center gap-1 mb-6">
                {[...Array(visible[idx % visible.length]?.rating || 5)].map((_, j) => (
                  <Star key={j} className="w-5 h-5 text-yellow-400 fill-yellow-400" />
                ))}
              </div>
              <p className="text-white text-xl sm:text-2xl font-medium leading-relaxed mb-8 italic">
                "{visible[idx % visible.length]?.content}"
              </p>
              <div className="flex items-center justify-center gap-4">
                <div className="w-14 h-14 rounded-full bg-gradient-to-br from-[#1E3A5F] to-[#2D5FA6] flex items-center justify-center text-white font-black text-lg border-2 border-orange-500/30">
                  {visible[idx % visible.length]?.avatar_initials || visible[idx % visible.length]?.name?.slice(0,2).toUpperCase()}
                </div>
                <div className="text-left">
                  <div className="text-white font-bold">{visible[idx % visible.length]?.name}</div>
                  <div className="text-slate-400 text-sm">{visible[idx % visible.length]?.role} · {visible[idx % visible.length]?.company}</div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Navigation dots + arrows */}
        <div className="flex items-center justify-center gap-4">
          <button onClick={() => setIdx(p => (p - 1 + visible.length) % visible.length)} className="w-9 h-9 rounded-full bg-white/8 border border-white/12 hover:bg-white/15 flex items-center justify-center text-white transition-all">
            <ChevronLeft className="w-4 h-4" />
          </button>
          <div className="flex gap-2">
            {visible.map((_, i) => (
              <button key={i} onClick={() => setIdx(i)} className={`rounded-full transition-all duration-300 ${i === idx % visible.length ? 'w-6 h-2 bg-orange-500' : 'w-2 h-2 bg-white/20 hover:bg-white/40'}`} />
            ))}
          </div>
          <button onClick={() => setIdx(p => (p + 1) % visible.length)} className="w-9 h-9 rounded-full bg-white/8 border border-white/12 hover:bg-white/15 flex items-center justify-center text-white transition-all">
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>

        {/* Grid preview otros testimonios */}
        <div className="grid md:grid-cols-3 gap-5 mt-12">
          {visible.slice(0, 3).map((t, i) => (
            <div
              key={i}
              className={`bg-white/3 border border-white/8 rounded-[12px] p-6 card-hover cursor-pointer reveal ${inView ? 'visible' : ''}`}
              style={{ transitionDelay: `${i * 100 + 400}ms` }}
              onClick={() => setIdx(i)}
            >
              <div className="flex gap-1 mb-3">
                {[...Array(t.rating || 5)].map((_, j) => <Star key={j} className="w-3.5 h-3.5 text-yellow-400 fill-yellow-400" />)}
              </div>
              <p className="text-slate-300 text-sm leading-relaxed mb-4 line-clamp-3">"{t.content}"</p>
              <div className="flex items-center gap-2.5">
                <div className="w-8 h-8 bg-[#1E3A5F] rounded-full flex items-center justify-center text-white text-xs font-bold flex-shrink-0">
                  {t.avatar_initials || t.name?.slice(0,2).toUpperCase()}
                </div>
                <div>
                  <div className="text-white text-xs font-semibold">{t.name}</div>
                  <div className="text-slate-500 text-xs">{t.company}</div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

/* ─── 8. PRECIOS ─────────────────────────────────────────────────── */
const Pricing = ({ navigate, conversion }) => {
  const [ref, inView] = useInView(0.1);
  const [annual, setAnnual] = useState(false);

  const plans = [
    {
      id: 'start', name: 'START', monthly: 59,
      users: '1 usuario', desc: 'Para el autónomo que quiere profesionalizarse',
      style: 'border-slate-200 bg-white', ctaStyle: 'bg-slate-900 hover:bg-slate-800 text-white',
      features: ['Dashboard', 'Clientes', 'Presupuestos PDF', 'Obras', 'Facturas', 'Soporte email'],
    },
    {
      id: 'pro', name: 'PRO', monthly: 119,
      users: '5 usuarios', desc: 'Para equipos pequeños con IA incluida',
      style: 'border-[#2D5FA6] bg-[#2D5FA6]/3', ctaStyle: 'bg-[#2D5FA6] hover:bg-[#1E3A5F] text-white',
      features: ['Todo lo de START', 'Stock / Materiales', 'OCR de facturas', 'MANDO IA Chat', 'Calculadora ROI', 'Soporte prioritario'],
    },
    {
      id: 'business', name: 'BUSINESS', monthly: 179,
      users: '8 usuarios', desc: 'Para empresas con múltiples obras simultáneas',
      style: 'border-orange-500', ctaStyle: 'bg-[#FF6B35] hover:bg-[#E85A24] text-white',
      popular: true,
      features: ['Todo lo de PRO', 'OCR avanzado', 'Baremos de mercado', 'Pagos proveedores', 'Incidencias', 'Onboarding incluido'],
    },
    {
      id: 'elite', name: 'ELITE', monthly: 299,
      users: 'Usuarios ilimitados', desc: 'Para grupos empresariales y franquicias',
      style: 'border-slate-200 bg-white', ctaStyle: 'bg-slate-900 hover:bg-slate-800 text-white',
      features: ['Todo lo de BUSINESS', 'Asesor IA Premium', 'Multisede', 'API acceso', 'SLA garantizado', 'Account manager'],
    },
  ];

  return (
    <section id="precios" className="py-24 bg-slate-50" ref={ref}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className={`text-center mb-12 reveal ${inView ? 'visible' : ''}`}>
          <span className="text-orange-500 font-semibold text-sm uppercase tracking-wider">Planes simples</span>
          <h2 className="text-3xl sm:text-4xl font-black text-slate-900 mt-3 mb-4">
            Sin letra pequeña. Sin sorpresas.
          </h2>
          <p className="text-xl text-slate-500 mb-8">7 días gratis en todos los planes. Sin tarjeta. Cancela cuando quieras.</p>

          {/* Toggle */}
          <div className="pricing-toggle-pill mx-auto inline-flex">
            <button onClick={() => setAnnual(false)} className={`pricing-toggle-option ${!annual ? 'active' : ''}`}>Mensual</button>
            <button onClick={() => setAnnual(true)}  className={`pricing-toggle-option ${annual ? 'active' : ''} relative`}>
              Anual
              <span className="absolute -top-2.5 -right-2 bg-emerald-500 text-white text-[9px] font-black px-1.5 py-0.5 rounded-full">-20%</span>
            </button>
          </div>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5 items-start">
          {plans.map((plan, i) => (
            <div
              key={plan.id}
              className={`relative rounded-2xl p-6 border-2 ${plan.style} ${plan.popular ? 'plan-popular' : ''} reveal ${inView ? 'visible' : ''} card-hover`}
              style={{ transitionDelay: `${i * 80}ms` }}
            >
              {plan.popular && (
                <div className="absolute -top-3.5 left-1/2 -translate-x-1/2 bg-orange-500 text-white text-xs font-black px-4 py-1.5 rounded-full shadow-md shadow-orange-500/30 whitespace-nowrap">
                  ⭐ Más popular
                </div>
              )}
              <div className={`text-xs font-black uppercase tracking-widest mb-2 ${plan.popular ? 'text-orange-500' : 'text-slate-400'}`}>{plan.name}</div>
              <div className="flex items-baseline gap-1 mb-1">
                <span className="text-4xl font-black text-slate-900">{annual ? Math.round(plan.monthly * 0.8) : plan.monthly}€</span>
                <span className="text-slate-400 text-sm">/mes</span>
              </div>
              <div className="text-slate-500 text-xs mb-1">{plan.users}</div>
              <p className="text-slate-500 text-xs mb-5 leading-relaxed">{plan.desc}</p>
              <ul className="space-y-2.5 mb-6">
                {plan.features.map((f, j) => (
                  <li key={j} className="flex items-start gap-2 text-sm text-slate-700">
                    <Check className="w-4 h-4 text-emerald-500 flex-shrink-0 mt-0.5" />{f}
                  </li>
                ))}
              </ul>
              <Button
                size="sm"
                onClick={() => window.open(conversion.app_url || 'https://appmando.leapifyes.com/', '_blank')}
                className={`w-full rounded-[8px] font-semibold ${plan.ctaStyle}`}
              >
                Empieza gratis
              </Button>
            </div>
          ))}
        </div>

        {/* Garantía */}
        <div className={`flex flex-col sm:flex-row items-center justify-center gap-4 mt-10 reveal ${inView ? 'visible' : ''} delay-400`}>
          <div className="flex items-center gap-3 bg-white border border-slate-200 rounded-full px-6 py-3 shadow-sm">
            <Shield className="w-5 h-5 text-emerald-500 flex-shrink-0" />
            <span className="text-slate-700 text-sm font-semibold">30 días de devolución garantizada. Sin preguntas.</span>
          </div>
          <button onClick={() => navigate('/precios')} className="text-orange-500 hover:text-orange-600 font-semibold text-sm flex items-center gap-1 underline-offset-2 hover:underline">
            Ver comparativa completa <ArrowRight className="w-4 h-4" />
          </button>
        </div>
      </div>
    </section>
  );
};

/* ─── 9. TRUST SIGNALS ───────────────────────────────────────────── */
const TrustSignals = () => {
  const [ref, inView] = useInView(0.2);
  const signals = [
    { icon: Lock,       title: 'Datos 100% seguros',      desc: 'Servidores en Europa (AWS Frankfurt). Cifrado AES-256 en reposo y tránsito.' },
    { icon: Shield,     title: 'RGPD Compliant',          desc: 'Cumplimiento total del Reglamento General de Protección de Datos europeo.' },
    { icon: X,          title: 'Sin permanencia',          desc: 'Cancela en cualquier momento desde tu panel. Sin llamadas ni trámites.' },
    { icon: Phone,      title: 'Soporte humano en español', desc: 'Equipo real en Barcelona. Sin bots. Sin respuestas automáticas de IA.' },
    { icon: Globe,      title: 'Multi-tenant seguro',      desc: 'Arquitectura multi-empresa. Tus datos aislados completamente de otros clientes.' },
    { icon: Award,      title: 'Uptime 99.9%',             desc: 'Infraestructura de alta disponibilidad con monitoreo 24/7 y backups diarios.' },
  ];

  return (
    <section className="py-20 bg-white border-t border-slate-100" ref={ref}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className={`text-center mb-12 reveal ${inView ? 'visible' : ''}`}>
          <span className="text-orange-500 font-semibold text-sm uppercase tracking-wider">Confianza y seguridad</span>
          <h2 className="text-2xl sm:text-3xl font-black text-slate-900 mt-3">
            Tu empresa y tus datos, siempre protegidos.
          </h2>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {signals.map((s, i) => {
            const Icon = s.icon;
            return (
              <div
                key={i}
                className={`flex gap-4 p-5 bg-slate-50 border border-slate-200 rounded-[12px] card-hover reveal ${inView ? 'visible' : ''}`}
                style={{ transitionDelay: `${i * 80}ms` }}
              >
                <div className="w-10 h-10 bg-emerald-100 rounded-xl flex items-center justify-center flex-shrink-0">
                  <Icon className="w-5 h-5 text-emerald-600" />
                </div>
                <div>
                  <h3 className="text-slate-900 font-bold text-sm mb-1">{s.title}</h3>
                  <p className="text-slate-500 text-sm leading-relaxed">{s.desc}</p>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

/* ─── 10. FAQ ────────────────────────────────────────────────────── */
const FAQ = () => {
  const [ref, inView] = useInView(0.1);
  const [open, setOpen] = useState(null);
  const [search, setSearch] = useState('');

  const faqs = [
    { q: '¿Puedo probar MANDO sin tarjeta de crédito?',           a: 'Sí. Los 15 días de prueba son completamente gratuitos y no necesitas tarjeta. Cuando se acabe el período, decides si continuar. Sin cobro automático.' },
    { q: '¿Cuánto tiempo lleva configurar MANDO?',                 a: 'La mayoría de empresas están operativas en menos de 2 horas. Incluimos un onboarding guiado y soporte en español para ayudarte en los primeros pasos.' },
    { q: '¿Puedo importar mis datos desde Excel?',                 a: 'Sí. Tienes importadores para clientes, servicios y artículos. También puedes exportar todo en cualquier momento en formato Excel o PDF.' },
    { q: '¿La IA de MANDO necesita formación especial?',           a: 'No. La IA tiene acceso a tus datos desde el primer día y funciona con preguntas en lenguaje natural. No necesitas ninguna configuración técnica.' },
    { q: '¿Puedo usar MANDO desde el móvil en la obra?',           a: 'Sí. MANDO funciona en cualquier dispositivo (iOS, Android, tablet). El interfaz está optimizado para móvil para usarlo directamente en la obra.' },
    { q: '¿Qué pasa con mis datos si cancelo?',                    a: 'Puedes exportar todos tus datos en cualquier momento en formato Excel/PDF. Tras la cancelación, tus datos se conservan 90 días por si cambias de opinión.' },
    { q: '¿Cuántos usuarios puedo añadir?',                        a: 'Depende del plan: START (1 usuario), PRO (5), BUSINESS (8) y ELITE (ilimitados). Puedes subir de plan en cualquier momento sin perder datos.' },
    { q: '¿MANDO funciona para autónomos o solo para empresas?',   a: 'MANDO funciona para ambos. El plan START está diseñado específicamente para autónomos que quieren profesionalizarse sin complejidad ni sobrecoste.' },
    { q: '¿Qué diferencia a MANDO de Holded o Fixner?',           a: 'MANDO está construido 100% para reformas y construcción, no es un ERP genérico adaptado. La IA conoce el sector, los módulos son específicos (obras, partidas, baremos) y el precio es más accesible.' },
    { q: '¿Hay descuento por pago anual?',                         a: 'Sí, el pago anual tiene un 20% de descuento sobre el precio mensual. Además, el soporte es prioritario en todos los planes con facturación anual.' },
  ];

  const filtered = faqs.filter(f => f.q.toLowerCase().includes(search.toLowerCase()) || f.a.toLowerCase().includes(search.toLowerCase()));

  return (
    <section id="faq" className="py-24 bg-slate-50" ref={ref}>
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className={`text-center mb-12 reveal ${inView ? 'visible' : ''}`}>
          <span className="text-orange-500 font-semibold text-sm uppercase tracking-wider">Preguntas frecuentes</span>
          <h2 className="text-3xl sm:text-4xl font-black text-slate-900 mt-3 mb-8">
            Todo lo que necesitas saber
          </h2>
          {/* Search */}
          <div className="relative">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <input
              type="text"
              placeholder="Buscar pregunta..."
              value={search}
              onChange={e => setSearch(e.target.value)}
              className="w-full pl-11 pr-4 py-3.5 bg-white border border-slate-200 rounded-[12px] text-slate-700 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-orange-500/30 focus:border-orange-500 transition-all shadow-sm"
            />
          </div>
        </div>

        <div className="space-y-3">
          {filtered.length === 0 && (
            <p className="text-center text-slate-400 py-8">No encontramos resultados para "{search}"</p>
          )}
          {filtered.map((faq, i) => (
            <div
              key={i}
              className={`bg-white border border-slate-200 rounded-[12px] overflow-hidden shadow-sm reveal ${inView ? 'visible' : ''}`}
              style={{ transitionDelay: `${i * 50}ms` }}
            >
              <button
                onClick={() => setOpen(open === i ? null : i)}
                className="w-full flex items-center justify-between gap-4 p-5 text-left hover:bg-slate-50 transition-colors"
                aria-expanded={open === i}
              >
                <span className="text-slate-900 font-semibold text-sm sm:text-base">{faq.q}</span>
                <div className={`w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 transition-all duration-200 ${open === i ? 'bg-orange-500 text-white' : 'bg-slate-100 text-slate-500'}`}>
                  {open === i ? <Minus className="w-3.5 h-3.5" /> : <Plus className="w-3.5 h-3.5" />}
                </div>
              </button>
              <div className={`faq-content ${open === i ? 'open' : ''}`}>
                <div className="px-5 pb-5 text-slate-600 text-sm sm:text-base leading-relaxed border-t border-slate-100 pt-4">
                  {faq.a}
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Schema FAQ JSON-LD */}
        <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'FAQPage',
          mainEntity: faqs.map(f => ({
            '@type': 'Question',
            name: f.q,
            acceptedAnswer: { '@type': 'Answer', text: f.a }
          }))
        }) }} />
      </div>
    </section>
  );
};

/* ─── 11. FINAL CTA ──────────────────────────────────────────────── */
const FinalCTA = ({ conversion }) => {
  const [ref, inView] = useInView(0.3);
  return (
    <section className="py-28 relative overflow-hidden" ref={ref} style={{ background: 'linear-gradient(135deg, #1E3A5F 0%, #0d1f3c 40%, #0a0f1e 100%)' }}>
      <div className="absolute inset-0 bg-[linear-gradient(to_right,#2D5FA620_1px,transparent_1px),linear-gradient(to_bottom,#2D5FA620_1px,transparent_1px)] bg-[size:60px_60px]" />
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[500px] h-[300px] bg-orange-500/8 rounded-full blur-3xl" />

      <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
        <div className={`reveal ${inView ? 'visible' : ''}`}>
          <h2 className="text-3xl sm:text-5xl font-black text-white mb-6 leading-tight">
            Cada semana sin MANDO<br />
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-orange-400 to-orange-600">
              es dinero que no recuperas.
            </span>
          </h2>
          <p className="text-xl text-blue-200 mb-10 max-w-xl mx-auto">
            15 días gratis. Sin tarjeta. Sin compromiso.<br />
            Crea tu primer presupuesto en menos de 5 minutos.
          </p>

          <div className="flex flex-col sm:flex-row gap-4 justify-center mb-10">
            <Button
              size="lg"
              onClick={() => window.open(conversion.app_url || 'https://appmando.leapifyes.com/', '_blank')}
              className="bg-[#FF6B35] hover:bg-[#E85A24] text-white font-black px-10 py-6 text-lg rounded-[8px] hover:scale-105 transition-all duration-200 hover:shadow-xl hover:shadow-orange-500/30 group"
              data-testid="final-cta-btn"
            >
              Empieza gratis ahora
              <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </Button>
            <Button
              size="lg"
              variant="outline"
              onClick={() => window.open(conversion.demo_url || 'https://ricardoserrano-leapifyes.zohobookings.eu/#/242621000000201008', '_blank')}
              className="border-white/25 text-white hover:bg-white/10 hover:border-white/40 px-10 py-6 text-lg rounded-[8px]"
            >
              Hablar con el equipo
            </Button>
          </div>

          <div className="flex flex-wrap items-center justify-center gap-6 text-blue-300/70 text-sm">
            <span className="flex items-center gap-1.5"><Check className="w-4 h-4 text-emerald-400" />Sin tarjeta de crédito</span>
            <span className="flex items-center gap-1.5"><Check className="w-4 h-4 text-emerald-400" />15 días completos gratis</span>
            <span className="flex items-center gap-1.5"><Check className="w-4 h-4 text-emerald-400" />Soporte en español</span>
            <span className="flex items-center gap-1.5"><Check className="w-4 h-4 text-emerald-400" />Cancela cuando quieras</span>
          </div>

          <p className="mt-8 text-blue-300/40 text-sm">
            +320 empresas de reformas y construcción ya confían en MANDO · Barcelona · Leapifyes
          </p>
        </div>
      </div>
    </section>
  );
};

/* ─── MAIN PAGE ──────────────────────────────────────────────────── */
export default function HomePage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [conversion, setConversion] = useState({
    app_url: 'https://appmando.leapifyes.com/',
    demo_url: 'https://ricardoserrano-leapifyes.zohobookings.eu/#/242621000000201008',
  });

  useAnalytics('Inicio - MANDO by Leapifyes');
  const { get } = usePageContent('home');

  useEffect(() => {
    window.scrollTo(0, 0);
    fetch(`${API_URL}/api/public/conversion-settings`)
      .then(r => r.ok ? r.json() : null)
      .then(d => { if (d) setConversion(prev => ({ ...prev, ...d })); })
      .catch(() => {});
  }, []);

  return (
    <>
      <SEOHead
        title="Software de Gestión de Obras y Reformas | MANDO ERP SaaS España"
        description="MANDO muestra el margen real de cada obra en tiempo real. ERP SaaS para empresas de reformas, obras y construcción. Presupuestos PDF, IA integrada, control de costes. Prueba 15 días gratis."
        canonical="https://mando.leapifyes.com/"
        schema={[SCHEMAS.softwareApp, SCHEMAS.organization, SCHEMAS.faq([
          { q: '¿Puedo probar MANDO sin tarjeta de crédito?', a: 'Sí. Los 15 días de prueba son completamente gratuitos y no necesitas tarjeta.' },
          { q: '¿Cuánto tiempo lleva configurar MANDO?', a: 'La mayoría de empresas están operativas en menos de 2 horas.' },
          { q: '¿Qué diferencia a MANDO de Holded o Fixner?', a: 'MANDO está construido 100% para reformas y construcción, con IA que conoce tus datos reales.' },
          { q: '¿Puedo usar MANDO desde el móvil en la obra?', a: 'Sí. MANDO funciona en cualquier dispositivo: iOS, Android y tablet.' },
          { q: '¿Hay descuento por pago anual?', a: 'Sí, el pago anual tiene un 20% de descuento sobre el precio mensual.' }
        ])]}
      />
      <div className="min-h-screen" style={{ background: '#0a0f1e' }}>

      <Header />
      <main>
        <CinematicHero
          brandName="MANDO"
          tagline1="Tu obra."
          tagline2="Sin el caos."
          cardHeading="Construye más. Gestiona mejor."
          metricValue={127}
          metricLabel="Proyectos activos"
          ctaHeading="Toma el control de tu obra."
          ctaDescription="Únete a las empresas constructoras y reformistas de Barcelona que ya gestionan sus proyectos con MANDO. Sin complicaciones, sin caos."
        />
        <MetricsBand />
        <Problems />
        <AISection      navigate={navigate} />
        <Modules        navigate={navigate} />
        <Comparison />
        <Testimonials />
        <Pricing        navigate={navigate} conversion={conversion} />
        <TrustSignals />
        <FAQ />
        <FinalCTA       conversion={conversion} />
      </main>
      <Footer />
      <WhatsAppButton />
      <CookieBanner />
    </div>
    </>
  );
}
